import { type ComponentPropsWithoutRef, type HTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';
import { FormFieldContext, FormItemContext, useFormField } from './hooks';
interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  name: string;
  error?: string;
  touched?: boolean;
  required?: boolean;
}
const FormField = ({
  name,
  error,
  touched,
  required,
  ...props
}: FormFieldProps) => <FormFieldContext.Provider value={{
  name,
  error,
  touched,
  required
}}>
    <div {...props} />
  </FormFieldContext.Provider>;
const FormItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({
  className,
  ...props
}, ref) => {
  const id = useId();
  return <FormItemContext.Provider value={{
    id
  }}>
        <div ref={ref} className={cn('space-y-2', className)} {...props} />
      </FormItemContext.Provider>;
});
FormItem.displayName = 'FormItem';
const FormLabel = forwardRef<HTMLLabelElement, ComponentPropsWithoutRef<'label'>>(({
  className,
  ...props
}, ref) => {
  const {
    error,
    formItemId
  } = useFormField();
  return <label ref={ref} className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', error && 'text-destructive', className)} htmlFor={formItemId} {...props}>
      {props.children}
    </label>;
});
FormLabel.displayName = 'FormLabel';
const FormControl = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({
  className,
  ...props
}, ref) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  return <div ref={ref} id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} className={cn('relative w-full', className)} {...props} />;
});
FormControl.displayName = 'FormControl';
const FormDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(({
  className,
  ...props
}, ref) => {
  const {
    formDescriptionId
  } = useFormField();
  return <p ref={ref} id={formDescriptionId} className={cn('text-[0.8rem] text-muted-foreground', className)} {...props} />;
});
FormDescription.displayName = 'FormDescription';
const FormMessage = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(({
  className,
  children,
  ...props
}, ref) => {
  const {
    error,
    touched,
    formMessageId
  } = useFormField();
  const body = touched && error ? error : children;
  if (!body) {
    return null;
  }
  return <p ref={ref} id={formMessageId} className={cn('text-[0.8rem] font-medium text-destructive', className)} {...props}>
      {body}
    </p>;
});
FormMessage.displayName = 'FormMessage';
export { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage };